import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useApiFetch — resilient fetch with progressive retries + silent background recovery.
 *
 * Timeline on a failing request:
 *   0ms         → attempt 1
 *   900ms       → attempt 2 (silent, still shows loading)
 *   ~3.4s total → attempt 3 (silent, still shows loading)
 *   after that  → sets error=true, then SILENTLY polls in the background with
 *                 exponential backoff (6s → 12s → 24s → 48s → 60s cap) until
 *                 the backend recovers. Delay resets to 6s on any success or
 *                 on manual retry.
 *
 * Also refetches when the window regains focus if we're currently missing data
 * or in error state — critical for laptops/phones returning from sleep.
 *
 * `fetcher` MUST be memoised (useCallback) by the caller.
 * `deps` is the caller-owned invalidation array (e.g. [slug]).
 */
const IMMEDIATE_DELAYS_MS = [0, 900, 2500];
const BG_POLL_INITIAL_MS = 6000;
const BG_POLL_MAX_MS = 60000;
const BG_POLL_MULTIPLIER = 2;

export function useApiFetch(fetcher, { deps = [] } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const cancelled = useRef(false);
  const bgTimer = useRef(null);
  const bgDelay = useRef(BG_POLL_INITIAL_MS);

  const clearBgTimer = () => {
    if (bgTimer.current) {
      clearTimeout(bgTimer.current);
      bgTimer.current = null;
    }
  };

  const tryOnce = useCallback(async () => {
    try {
      return { ok: true, value: await fetcher() };
    } catch (e) {
      return { ok: false, err: e };
    }
  }, [fetcher]);

  const run = useCallback(async () => {
    cancelled.current = false;
    clearBgTimer();
    bgDelay.current = BG_POLL_INITIAL_MS; // reset backoff on every fresh run
    setLoading(true);
    setError(false);

    for (let i = 0; i < IMMEDIATE_DELAYS_MS.length; i++) {
      const delay = IMMEDIATE_DELAYS_MS[i];
      if (delay > 0) {
        await new Promise((r) => setTimeout(r, delay));
      }
      if (cancelled.current) return;
      const result = await tryOnce();
      if (cancelled.current) return;
      if (result.ok) {
        setData(result.value);
        setLoading(false);
        setError(false);
        bgDelay.current = BG_POLL_INITIAL_MS;
        return;
      }
    }

    // All immediate attempts failed → surface error, keep polling silently
    // with exponential backoff (6s → 12s → 24s → 48s → 60s cap).
    if (cancelled.current) return;
    setError(true);
    setLoading(false);

    const poll = async () => {
      if (cancelled.current) return;
      const result = await tryOnce();
      if (cancelled.current) return;
      if (result.ok) {
        setData(result.value);
        setError(false);
        bgDelay.current = BG_POLL_INITIAL_MS; // reset on success
        return;
      }
      bgDelay.current = Math.min(bgDelay.current * BG_POLL_MULTIPLIER, BG_POLL_MAX_MS);
      bgTimer.current = setTimeout(poll, bgDelay.current);
    };
    bgTimer.current = setTimeout(poll, bgDelay.current);
  }, [tryOnce]);

  useEffect(() => {
    run();
    return () => {
      cancelled.current = true;
      clearBgTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Re-fetch on window focus if we're missing data or in error state
  // (handles backend cold-start + sleeping laptop scenarios).
  useEffect(() => {
    const onFocus = () => {
      if (!data || error) run();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [data, error, run]);

  return { data, loading, error, retry: run };
}
